import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { DashboardDateControl } from '@/components/dashboard/DashboardDateControl';
import { readShigoAppConfig } from '@/lib/config';
import { useShigoSession } from '@/lib/useShigoSession';
import { DashboardMetricCard } from '../../../dashboard/components/DashboardMetricCard';
import { DashboardPerformanceChart } from '../../../dashboard/components/DashboardPerformanceChart';
import {
  dashboardRouteSearchSchema,
  parseDashboardRouteSearch,
  serializeDashboardRouteFilters,
} from '../../../dashboard/dashboardRouteSearch';
import { useAgencyCampaignContext } from '../../agencyCampaignContext';
import {
  campaignOverviewMetrics,
  createCampaignOverviewClient,
  type CampaignOverviewFilter,
} from '../../overviewReporting';

export function CampaignOverviewMetrics({ filter }: { filter: CampaignOverviewFilter }) {
  const scope = useAgencyCampaignContext();
  const { session } = useShigoSession();
  const config = readShigoAppConfig();
  const search = useSearch({ strict: false });
  const navigate = useNavigate({ from: overviewRoute(filter, Boolean(scope)) });
  const { range } = parseDashboardRouteSearch(dashboardRouteSearchSchema(search));
  const query = useQuery({
    queryKey: [
      'campaign-overview',
      config.networkId,
      scope?.agency.id,
      filter,
      range.dateFrom,
      range.dateTo,
    ],
    queryFn: () =>
      createCampaignOverviewClient({
        config: { apiBaseUrl: config.apiBaseUrl, networkId: config.networkId },
        accessToken: session?.accessToken ?? '',
      }).report(range, filter, scope?.agency.id),
    enabled: Boolean(session?.accessToken),
  });
  return (
    <section className="mb-6 min-w-0 space-y-6">
      <div className="flex justify-end">
        <DashboardDateControl
          value={range}
          showDayNavigation={range.dateFrom === range.dateTo}
          onChange={(next) => {
            void navigate({
              params: true,
              search: (current) => {
                const nextSearch = { ...current };
                delete nextSearch.preset;
                delete nextSearch.from;
                delete nextSearch.to;
                return { ...nextSearch, ...serializeDashboardRouteFilters({ range: next }) };
              },
              replace: true,
            });
          }}
        />
      </div>
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {campaignOverviewMetrics.map((metric) => (
          <DashboardMetricCard
            key={metric}
            metric={metric}
            total={query.data?.total}
            loading={query.isLoading}
            error={query.isError}
            actions={[]}
          />
        ))}
      </div>
      <DashboardPerformanceChart
        overview={query.data}
        metrics={campaignOverviewMetrics}
        range={range}
        loading={query.isLoading}
        error={query.isError}
        actions={[]}
      />
    </section>
  );
}

function overviewRoute(filter: CampaignOverviewFilter, scoped: boolean) {
  if (filter.agencyIds)
    return scoped ? '/a/$agencyId/campaigns/overview' : '/campaigns/agencies/$agencyId/details';
  if (filter.msBrandIds)
    return scoped
      ? '/a/$agencyId/campaigns/brands/$brandId/details'
      : '/campaigns/brands/$brandId/details';
  return scoped ? '/a/$agencyId/campaigns/$section' : '/campaigns/campaigns/overview/$campaignId';
}
